import { cloneElement, useCallback, useMemo, useState } from "react";
import {
  PaginationDots,
  type GeneralBarDatum,
  type TotalsAnnotationsValues,
} from "@jana-technology/platform-ui";

import { PAGE_SIZE } from "constants/charts";

import type { PaginatedChartProps } from "./types";
import { annotationsSlicer, sliceChartData } from "./helpers";

export const PaginatedChart = <
  TData extends GeneralBarDatum = GeneralBarDatum,
  TAnnotations extends TotalsAnnotationsValues = TotalsAnnotationsValues,
>({
  length,
  defaultIndex = 0,
  groupSize = 1,
  data,
  annotationsValues,
  children,
  dotsTopOffset = 480,
  onPaginationChange,
}: PaginatedChartProps<TData, TAnnotations>) => {
  const [activeIndex, setActiveIndex] = useState(defaultIndex);

  const paginatedChart = useMemo(() => {
    return {
      data: Array.isArray(data)
        ? sliceChartData(data, activeIndex, groupSize)
        : [],
      annotationsValues: Array.isArray(annotationsValues)
        ? annotationsSlicer(annotationsValues, activeIndex)
        : undefined,
    };
  }, [data, activeIndex, groupSize, annotationsValues]);

  const onChange = useCallback(
    (index: number) => {
      setActiveIndex(index);
      onPaginationChange?.(index);
    },
    [onPaginationChange],
  );

  return (
    <div className="relative">
      {cloneElement(children, paginatedChart)}
      <div
        className="absolute inset-x-0 flex justify-center"
        style={{ top: dotsTopOffset }}
      >
        <PaginationDots
          length={length ?? Math.ceil(data.length / (PAGE_SIZE * groupSize))}
          defaultIndex={defaultIndex}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

export default PaginatedChart;
